import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Drawer, message } from "antd";
import cx from "classnames";
import { Hooks, Request, Util } from "@xt/xtbase";
import XtSvg from "@/components/Xt/Svg";
import { put_cfd_positionTpSl } from "api/v4/cfd";
import useCfdConfig from "../../_hook/useCfdConfig";
import styles from "./stopModal.module.scss";

const { useTranslation } = Hooks;
const { Big } = Util;

interface StopModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  position?: {
    id: string;
    pair: string;
    side: "buy" | "sell";
    leverage: string;
    entryPrice?: string;
    currentPrice?: string;
    quantity?: string;
    stopProfit?: string;
    stopLoss?: string;
  };
}

const StopModal: React.FC<StopModalProps> = ({ open, onClose, onSuccess, position }) => {
  const t = useTranslation();
  const cfdConfig = useCfdConfig();
  const [takeProfitPrice, setTakeProfitPrice] = useState("");
  const [stopLossPrice, setStopLossPrice] = useState("");
  const [loading, setLoading] = useState(false);

  const sideLabel = useMemo(() => (position?.side === "buy" ? t("cfd.buyUp") : t("cfd.buyDown")), [position?.side, t]);

  // 处理止盈价格输入，过滤非数字
  const handleTakeProfitInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newVal = e.target.value;
    
    // 去掉千分位逗号
    newVal = newVal.replace(/,/g, "");
    
    // 允许空字符串
    if (newVal === "") {
      setTakeProfitPrice("");
      return;
    }
    
    // 只允许数字和小数点，允许小数点开头（如 .5）
    if (!/^[0-9]*\.?[0-9]*$/.test(newVal)) {
      return;
    }
    
    // 不允许多个小数点
    if ((newVal.match(/\./g) || []).length > 1) {
      return;
    }
    
    // 不允许以0开头（除了0.或0或.开头）
    if (/^0[0-9]/.test(newVal)) {
      return;
    }
    
    // 限制小数位数（最多8位）
    if (/\./.test(newVal)) {
      const decimalPart = newVal.split(".")[1];
      if (decimalPart && decimalPart.length > 8) {
        return;
      }
    }
    
    setTakeProfitPrice(newVal);
  }, []);

  // 处理止损价格输入，过滤非数字
  const handleStopLossInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newVal = e.target.value;
    
    // 去掉千分位逗号
    newVal = newVal.replace(/,/g, "");
    
    // 允许空字符串
    if (newVal === "") {
      setStopLossPrice("");
      return;
    }
    
    // 只允许数字和小数点，允许小数点开头（如 .5）
    if (!/^[0-9]*\.?[0-9]*$/.test(newVal)) {
      return;
    }
    
    // 不允许多个小数点
    if ((newVal.match(/\./g) || []).length > 1) {
      return;
    }
    
    // 不允许以0开头（除了0.或0或.开头）
    if (/^0[0-9]/.test(newVal)) {
      return;
    }
    
    // 限制小数位数（最多8位）
    if (/\./.test(newVal)) {
      const decimalPart = newVal.split(".")[1];
      if (decimalPart && decimalPart.length > 8) {
        return;
      }
    }
    
    setStopLossPrice(newVal);
  }, []);

  // 当弹窗打开时，初始化止盈止损价格
  useEffect(() => {
    // if (open && position) {
    //   // 去掉千分位逗号后再赋值
    //   const stopProfit = position.stopProfit && position.stopProfit !== "--" 
    //     ? position.stopProfit.replace(/,/g, "") 
    //     : "";
    //   const stopLoss = position.stopLoss && position.stopLoss !== "--" 
    //     ? position.stopLoss.replace(/,/g, "") 
    //     : "";
    //   setTakeProfitPrice(stopProfit);
    //   setStopLossPrice(stopLoss);
    // }
  }, [open, position]);

  const handleConfirm = useCallback(async () => {
    if (!position?.id) {
      message.error(t("cfd.updateFailed") || "Position ID is required");
      return;
    }

    // 清理并验证止盈止损价格（去掉逗号、空格等）
    const cleanTakeProfit = takeProfitPrice ? takeProfitPrice.replace(/,/g, "").trim() : "";
    const cleanStopLoss = stopLossPrice ? stopLossPrice.replace(/,/g, "").trim() : "";

    // 验证：至少需要填写一个价格（止盈或止损）
    let hasTakeProfit = false;
    let hasStopLoss = false;
    let tpPriceNum: any = null;
    let slPriceNum: any = null;

    if (cleanTakeProfit) {
      try {
        tpPriceNum = Big(cleanTakeProfit);
        hasTakeProfit = tpPriceNum.gt(0);
      } catch (e) {
        message.error(t("cfd.invalidTakeProfitPrice") || "Invalid take profit price");
        return;
      }
    }

    if (cleanStopLoss) {
      try {
        slPriceNum = Big(cleanStopLoss);
        hasStopLoss = slPriceNum.gt(0);
      } catch (e) {
        message.error(t("cfd.invalidStopLossPrice") || "Invalid stop loss price");
        return;
      }
    }

    if (!hasTakeProfit && !hasStopLoss) {
      message.error(t("cfd.pleaseEnterTpSl") || "Please enter at least one price (take profit or stop loss)");
      return;
    }

    // 验证价格范围
    const minPrice = 0.01;
    if (hasTakeProfit && tpPriceNum.lt(minPrice)) {
      message.error(t("cfd.priceMustBeGreaterThan001") || "Price must be >= 0.01");
      return;
    }
    if (hasStopLoss && slPriceNum.lt(minPrice)) {
      message.error(t("cfd.priceMustBeGreaterThan001") || "Price must be >= 0.01");
      return;
    }

    // 验证止盈止损范围限制（如果配置了）
    if (cfdConfig.tpSlRange && position?.currentPrice) {
      // 清理 currentPrice（可能包含逗号）
      const cleanCurrentPrice = position.currentPrice.replace(/,/g, "").trim();
      if (!cleanCurrentPrice || cleanCurrentPrice === "--") {
        message.error(t("cfd.invalidCurrentPrice") || "Invalid current price");
        return;
      }

      let currentPriceNum: any;
      try {
        currentPriceNum = Big(cleanCurrentPrice);
      } catch (e) {
        message.error(t("cfd.invalidCurrentPrice") || "Invalid current price");
        return;
      }

      const { max, min } = cfdConfig.tpSlRange;
      
      // if (hasTakeProfit) {
      //   // 计算价格变化百分比
      //   const priceChangePercent = position.side === "buy" 
      //     ? tpPriceNum.minus(currentPriceNum).div(currentPriceNum).times(100).abs()
      //     : currentPriceNum.minus(tpPriceNum).div(currentPriceNum).times(100).abs();
        
      //   if (priceChangePercent.gt(max)) {
      //     message.error(t("cfd.tpExceedsMaxRange") || `Take profit exceeds maximum range: ${max}%`);
      //     return;
      //   }
      //   if (priceChangePercent.lt(min)) {
      //     message.error(t("cfd.tpBelowMinRange") || `Take profit below minimum range: ${min}%`);
      //     return;
      //   }
      // }
      
      // if (hasStopLoss) {
      //   // 计算价格变化百分比
      //   const priceChangePercent = position.side === "buy"
      //     ? currentPriceNum.minus(slPriceNum).div(currentPriceNum).times(100).abs()
      //     : slPriceNum.minus(currentPriceNum).div(currentPriceNum).times(100).abs();
        
      //   if (priceChangePercent.gt(max)) {
      //     message.error(t("cfd.slExceedsMaxRange") || `Stop loss exceeds maximum range: ${max}%`);
      //     return;
      //   }
      //   if (priceChangePercent.lt(min)) {
      //     message.error(t("cfd.slBelowMinRange") || `Stop loss below minimum range: ${min}%`);
      //     return;
      //   }
      // }
    }

    setLoading(true);
    try {
      const data: any = {};
      if (hasTakeProfit && tpPriceNum) {
        data.takeProfitPrice = Number(cleanTakeProfit);
      }
      if (hasStopLoss && slPriceNum) {
        data.stopLossPrice = Number(cleanStopLoss);
      }

      const { err } = await Request.XtAxios.asyncAwait(() => 
        put_cfd_positionTpSl(position.id, {
          data,
        })
      );

      if (err) {
        message.error(err.msg || t("cfd.updateFailed") || "Update failed");
        return;
      }

      // 更新成功
      message.success(t("cfd.updateSuccess") || "Update successful");
      onClose();
      onSuccess && onSuccess();
    } catch (error) {
      console.error("修改止盈止损失败:", error);
      message.error(t("cfd.updateFailed") || "Update failed");
    } finally {
      setLoading(false);
    }
  }, [position?.id, takeProfitPrice, stopLossPrice, t, onClose, onSuccess]);

  return (
    <Drawer
      placement="bottom"
      closable={false}
      open={open}
      onClose={onClose}
      className={styles.drawer}
      destroyOnClose
      title={<span className={styles.headerTitle}>{t("cfd.stopProfitLoss")}</span>}
      extra={
        <button className={cx("btnTxt", "btnHover")} onClick={onClose}>
          <XtSvg icon="close" />
        </button>
      }
    >

      <div className={styles.pairRow}>
        <span className={cx(styles.sideTag, position?.side === "buy" ? styles.buy : styles.sell)}>{sideLabel}</span>
        <span className={styles.pair}>{position?.pair || "--"}</span>
        <span className={styles.x}>{position?.leverage || ""}</span>
      </div>

      <div className={styles.prices}>
        <span>{t("cfd.entryAveragePrice")}：{position?.entryPrice || "--"}</span>
        <span>{t("cfd.latestPrice")}：{position?.currentPrice || "--"}</span>
      </div>

      <div className={styles.label}>{t("cfd.stopProfit")}</div>
      <div className={styles.row}>
        <input
          className={styles.input}
          placeholder={t("cfd.pleaseEnterPrice")}
          value={takeProfitPrice}
          onChange={handleTakeProfitInput}
          type="text"
        />
      </div>

      <div className={styles.label}>{t("cfd.stopLoss")}</div>
      <div className={styles.row}>
        <input
          className={styles.input}
          placeholder={t("cfd.pleaseEnterPrice")}
          value={stopLossPrice}
          onChange={handleStopLossInput}
          type="text"
        />
      </div>

      <div className={styles.positionQty}>{t("cfd.positionQuantity")}：{position?.quantity ? `${position.quantity}` : "--"}</div>

      <button className={styles.confirmBtn} onClick={handleConfirm} disabled={loading}>
        {loading ? t("cfd.submitting") || "Submitting..." : t("cfd.confirm")}
      </button>
    </Drawer>
  );
};

export default StopModal;
